/**
 * FirewallsList — Listado de firewalls, físicos y virtuales.
 *
 * Antes: cabecera, buscador y tabla propios (`wol-*`), un emoji por fabricante,
 * colores en duro, sin filtros, sin columnas elegibles y sin export con el
 * estándar. La ficha abría **directamente en edición** y el borrado solo
 * existía dentro de ella, con `window.confirm`.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** y el borrado vive en el listado con el diálogo común.
 *
 * Los dos datos que el firewall no guarda en su tabla los añade el backend en
 * `GET /api/firewalls` (`_add_links`):
 *   · `ip_count` — direcciones del IPAM asignadas al firewall.
 *   · `services` — servicios de los que forma parte. El vínculo está en DOS
 *     sitios y hay que unirlos: `service_hosts` («servicio en el que corre») y
 *     `sd_service_members` (nodos del grafo del Diseñador).
 *
 * Botones propios (en este orden):
 *  1. **Ver sus interfaces** — rol (WAN/LAN/DMZ/HA), conector, MAC, velocidad,
 *     VLAN y si está habilitada.
 *  2. **Ver sus conexiones** — a qué switch, patch panel o toma va cada
 *     interfaz, con el cable (tipo, color y metros). Sale del mismo endpoint
 *     que usa el canvas de red (`/ports`), que sí trae el otro extremo.
 *  3. **Ver sus IPs** — las asignadas en el IPAM, con salto a su ficha de IP.
 *  4. **Ver sus servicios** — si es uno solo va directo a su ficha; si son
 *     varios, abre el diálogo con la lista.
 *  5. **Ver el rack** (solo los físicos montados en uno) y **Ver la VM**
 *     (solo los virtuales).
 *  6. **Ver en el Diseñador** — el grafo centrado en este firewall.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowsRightLeftIcon,
  LinkIcon,
  GlobeAltIcon,
  RectangleGroupIcon,
  RectangleStackIcon,
  ComputerDesktopIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import Dialog from './common/Dialog';
import FirewallForm from './FirewallForm';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import useLocationPaths from '../hooks/useLocationPaths';
import { locationPathOptions } from '../utils/locationTree';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';

/** Estado del firewall: etiqueta y modificador del chip del estándar. */
const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  standby:     { label: 'Standby',       badge: 'list-badge--warn' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};

/** Rol dentro del par de alta disponibilidad. */
const HA_ROLE_LABELS = { primary: 'Primario', secondary: 'Secundario', standalone: 'HA' };

/** Firewall vacío: lo justo para que el formulario arranque en «alta». */
const EMPTY_FIREWALL = { name: '', deployment_type: 'physical', vendor: 'generic', status: 'active' };

/** Línea de los diálogos: fila de tokens (nunca `<table>` de Bootstrap, que
 *  dentro de un diálogo claro sale con fondo oscuro por los overrides del tema). */
function Row({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '6px 0', borderBottom: '1px solid var(--border)',
    }}>
      {children}
    </div>
  );
}

export default function FirewallsList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [firewalls, setFirewalls] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [bulkResetKey, setBulkResetKey] = useState(0);
  const [search, setSearch]       = useState('');

  const [filterStatus, setFilterStatus]       = useState('');
  const [filterDeploy, setFilterDeploy]       = useState('');
  const [filterVendor, setFilterVendor]       = useState('');
  const [filterAppliance, setFilterAppliance] = useState('');
  const [filterHa, setFilterHa]               = useState('');
  const [filterVpn, setFilterVpn]             = useState('');
  const [filterIds, setFilterIds]             = useState('');
  const [filterLocation, setFilterLocation]   = useState('');
  const [filterRack, setFilterRack]           = useState('');
  const [filterIfaces, setFilterIfaces]       = useState('');
  const [filterSerial, setFilterSerial]       = useState('');
  const [filterService, setFilterService]     = useState('');
  const [groupMode, setGroupMode]             = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [startClone, setStartClone]   = useState(false);
  const [hlId, setHlId]               = useState(null);

  /* Diálogos: qué firewall está abierto y qué datos tiene cargados. */
  const [ifacesOf, setIfacesOf]             = useState(null);
  const [ifaces, setIfaces]                 = useState([]);
  const [ifacesLoading, setIfacesLoading]   = useState(false);
  const [connsOf, setConnsOf]               = useState(null);
  const [conns, setConns]                   = useState([]);
  const [connsLoading, setConnsLoading]     = useState(false);
  const [ipsOf, setIpsOf]                   = useState(null);
  const [ips, setIps]                       = useState([]);
  const [ipsLoading, setIpsLoading]         = useState(false);
  const [servicesOf, setServicesOf]         = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      // Los filtros son de UI (estándar de listados): se pide la lista completa.
      const res = await api.getFirewalls(companyId);
      setFirewalls(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los firewalls');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB para llegar desde otra pantalla. */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !firewalls.length) return undefined;
    const id = Number(raw);
    const target = firewalls.find(f => f.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) {
      setStartClone(false);
      setInitialMode('view');
      setSelected(target);
      return undefined;
    }
    setHlId(id);
    const row = document.getElementById(`fw-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, firewalls, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (fw) => { setStartClone(false); setInitialMode('view');   setSelected(fw); };
  const openEdit   = (fw) => { setStartClone(false); setInitialMode('edit');   setSelected(fw); };
  const openCreate = ()   => { setStartClone(false); setInitialMode('create'); setSelected({ ...EMPTY_FIREWALL }); };
  /** Copiar abre la ficha en lectura con el diálogo de clonado del formulario,
   *  que es el que usa el endpoint `/clone` (se lleva interfaces y fuentes). */
  const openCopy   = (fw) => { setStartClone(true); setInitialMode('view'); setSelected(fw); };
  const closeForm  = ()   => { setSelected(null); setStartClone(false); load(); };
  useAutoOpen(firewalls, openView);

  const handleDelete = async (fw) => {
    try {
      await api.deleteFirewall(fw.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el firewall');
    }
  };

  /* ── diálogos ───────────────────────────────────────────────────────────── */
  const openIfaces = async (fw) => {
    setIfacesOf(fw); setIfaces([]); setIfacesLoading(true);
    try {
      const res = await api.getFirewallInterfaces(fw.id, companyId);
      setIfaces(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las interfaces');
    } finally {
      setIfacesLoading(false);
    }
  };

  const openConns = async (fw) => {
    setConnsOf(fw); setConns([]); setConnsLoading(true);
    try {
      const res = await api.getFirewallPorts(fw.id, companyId);
      setConns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las conexiones');
    } finally {
      setConnsLoading(false);
    }
  };

  const openIps = async (fw) => {
    setIpsOf(fw); setIps([]); setIpsLoading(true);
    try {
      const res = await api.getIpamAssignments(companyId, { object_type: 'firewall', object_id: fw.id });
      setIps(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las IPs asignadas');
    } finally {
      setIpsLoading(false);
    }
  };

  /** Con un solo servicio no hace falta diálogo: se va directo a su ficha. */
  const openServices = (fw) => {
    const lista = fw.services || [];
    if (lista.length === 1) {
      navigate(`/services?destacar=${lista[0].id}&abrir=1`);
      return;
    }
    setServicesOf(fw);
  };

  /* ── opciones de los filtros ────────────────────────────────────────────── */
  const vendorOptions = useMemo(() => {
    const mapa = new Map();
    firewalls.forEach(f => { if (f.vendor) mapa.set(f.vendor, f.vendor_label || f.vendor); });
    return [...mapa.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [firewalls]);

  const applianceOptions = useMemo(() => {
    const mapa = new Map();
    firewalls.forEach(f => {
      if (!f.appliance_type) return;
      mapa.set(f.appliance_type, f.appliance_type_label || f.appliance_type);
    });
    // «Ninguno» se llama de otra forma en el desplegable: aquí es «sin appliance».
    if (mapa.has('none')) mapa.set('none', tr('fw.no_appliance', 'Sin appliance (firmware propio)'));
    return [...mapa.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [firewalls, tr]);

  const { locations: todasUbicaciones, pathFor } = useLocationPaths(companyId);

  const locationOptions = useMemo(() => {
    const usados = new Set(firewalls.map(f => String(f.location_id)).filter(Boolean));
    const opciones = locationPathOptions(todasUbicaciones, { onlyIds: usados });
    if (!opciones.length) {
      // Sin árbol cargado (o sin permisos): al menos el nombre que trae el listado.
      const mapa = new Map();
      firewalls.forEach(f => { if (f.location_id) mapa.set(String(f.location_id), f.location_name || `#${f.location_id}`); });
      mapa.forEach((label, value) => opciones.push({ value, label }));
    }
    return [...opciones, { value: 'none', label: tr('fw.no_location', 'Sin ubicación asignada') }];
  }, [firewalls, todasUbicaciones, tr]);

  const rackOptions = useMemo(() => {
    const mapa = new Map();
    firewalls.forEach(f => { if (f.rack_id) mapa.set(String(f.rack_id), f.rack_name || `#${f.rack_id}`); });
    const opciones = [...mapa.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
    return [...opciones, { value: 'none', label: tr('fw.no_rack', 'Sin rack') }];
  }, [firewalls, tr]);

  const serviceOptions = useMemo(() => {
    const mapa = new Map();
    firewalls.forEach(f => (f.services || []).forEach(s => mapa.set(String(s.id), s.name || `#${s.id}`)));
    const opciones = [...mapa.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
    return [...opciones, { value: 'none', label: tr('fw.services_no', 'Sin servicio asignado') }];
  }, [firewalls, tr]);

  const hayRacks = useMemo(() => firewalls.some(f => f.rack_id), [firewalls]);

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return firewalls.filter((f) => {
      if (filterStatus && f.status !== filterStatus) return false;
      if (filterDeploy && (f.deployment_type || 'physical') !== filterDeploy) return false;
      if (filterVendor && f.vendor !== filterVendor) return false;
      if (filterAppliance && (f.appliance_type || '') !== filterAppliance) return false;
      if (filterHa === 'yes' && !f.ha_enabled) return false;
      if (filterHa === 'no' && f.ha_enabled) return false;
      if (filterHa === 'primary' && f.ha_role !== 'primary') return false;
      if (filterHa === 'secondary' && f.ha_role !== 'secondary') return false;
      if (filterVpn === 'yes' && !f.vpn_capable) return false;
      if (filterVpn === 'no' && f.vpn_capable) return false;
      if (filterIds === 'yes' && !f.ids_ips) return false;
      if (filterIds === 'no' && f.ids_ips) return false;
      if (filterLocation === 'none' && f.location_id) return false;
      if (filterLocation && filterLocation !== 'none' && String(f.location_id || '') !== filterLocation) return false;
      if (filterRack === 'none' && f.rack_id) return false;
      if (filterRack && filterRack !== 'none' && String(f.rack_id || '') !== filterRack) return false;
      if (filterIfaces === 'yes' && !f.interface_count) return false;
      if (filterIfaces === 'no' && f.interface_count) return false;
      if (filterSerial === 'yes' && !f.serial_number) return false;
      if (filterSerial === 'no' && f.serial_number) return false;
      if (filterService === 'none' && (f.services || []).length) return false;
      if (filterService && filterService !== 'none'
        && !(f.services || []).some(s => String(s.id) === filterService)) return false;
      if (!term) return true;
      return [
        f.name, f.hostname, f.model, f.vendor_label, f.appliance_type_label,
        f.serial_number, f.asset_tag, f.firmware_version, f.location_name,
        f.rack_name, f.vm_name, f.notes, f.status_label,
        ...(f.services || []).map(s => s.name),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [firewalls, search, filterStatus, filterDeploy, filterVendor, filterAppliance,
    filterHa, filterVpn, filterIds, filterLocation, filterRack, filterIfaces,
    filterSerial, filterService]);

  // Orden de las fichas congelado mientras la ficha está abierta (antes de
  // cualquier return temprano: un hook no puede ir detrás de uno).
  const navFirewalls = useNavOrder(filtered, selected, selected !== null);

  const clearFilters = () => {
    setFilterStatus(''); setFilterDeploy(''); setFilterVendor(''); setFilterAppliance('');
    setFilterHa(''); setFilterVpn(''); setFilterIds(''); setFilterLocation('');
    setFilterRack(''); setFilterIfaces(''); setFilterSerial(''); setFilterService('');
    setSearch('');
  };

  /* ── KPIs: chips que filtran al pulsarlos (estándar) ────────────────────── */
  const cuenta = useMemo(() => ({
    total:      firewalls.length,
    active:     firewalls.filter(f => f.status === 'active').length,
    standby:    firewalls.filter(f => f.status === 'standby').length,
    ha:         firewalls.filter(f => f.ha_enabled).length,
    virtual:    firewalls.filter(f => f.deployment_type === 'virtual').length,
    noIfaces:   firewalls.filter(f => !f.interface_count).length,
    noLocation: firewalls.filter(f => !f.location_id).length,
  }), [firewalls]);

  const kpis = (
    <div className="list-kpis list-kpis--chips">
      <button type="button" className="list-kpi-chip" onClick={clearFilters}
        title={tr('fw.kpi_all', 'Ver todos los firewalls')}>
        <span className="list-kpi-value">{cuenta.total}</span>
        <span className="list-kpi-label">{tr('fw.plural', 'Firewalls')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('active'); }}
        title={tr('fw.kpi_active', 'Ver los activos')}>
        <span className="list-kpi-value">{cuenta.active}</span>
        <span className="list-kpi-label">{tr('fw.active', 'Activos')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('standby'); }}
        title={tr('fw.kpi_standby', 'Ver los que están en standby')}>
        <span className="list-kpi-value">{cuenta.standby}</span>
        <span className="list-kpi-label">{tr('fw.standby', 'Standby')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterHa('yes'); }}
        title={tr('fw.kpi_ha', 'Ver los que están en alta disponibilidad')}>
        <span className="list-kpi-value">{cuenta.ha}</span>
        <span className="list-kpi-label">{tr('fw.ha', 'En HA')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterDeploy('virtual'); }}
        title={tr('fw.kpi_virtual', 'Ver los virtuales')}>
        <span className="list-kpi-value">{cuenta.virtual}</span>
        <span className="list-kpi-label">{tr('fw.virtual_plural', 'Virtuales')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterIfaces('no'); }}
        title={tr('fw.kpi_no_ifaces', 'Ver los que no tienen interfaces declaradas')}>
        <span className="list-kpi-value">{cuenta.noIfaces}</span>
        <span className="list-kpi-label">{tr('fw.without_ifaces', 'Sin interfaces')}</span>
      </button>
      <button type="button" className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterLocation('none'); }}
        title={tr('fw.kpi_no_location', 'Ver los que no tienen ubicación asignada')}>
        <span className="list-kpi-value">{cuenta.noLocation}</span>
        <span className="list-kpi-label">{tr('fw.without_location', 'Sin ubicación')}</span>
      </button>
    </div>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('fw.name', 'Nombre'),
        sortable: true,
        render: (f) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{f.name}</span>
            {f.hostname && <span className="list-hint list-code">{f.hostname}</span>}
          </span>
        ),
      },
      {
        key: 'deployment_type',
        label: tr('virt.type', 'Tipo'),
        sortable: true,
        sortValue: (f) => f.deployment_type || 'physical',
        render: (f) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
            <span className={`list-badge${f.deployment_type === 'virtual' ? ' list-badge--warn' : ''}`}>
              {f.deployment_type_label || (f.deployment_type === 'virtual'
                ? tr('fw.virtual', 'Virtual')
                : tr('fw.physical', 'Físico'))}
            </span>
            {f.vm_name && <span className="list-hint">{f.vm_name}</span>}
          </span>
        ),
      },
      {
        key: 'vendor',
        label: tr('fw.vendor', 'Fabricante'),
        sortable: true,
        sortValue: (f) => f.vendor_label || '',
        render: (f) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{f.vendor_label || dash}</span>
            {f.model && <span className="list-hint">{f.model}</span>}
          </span>
        ),
      },
      {
        key: 'appliance',
        label: tr('fw.appliance', 'Appliance'),
        sortable: true,
        sortValue: (f) => (f.appliance_type === 'none' ? '' : (f.appliance_type_label || '')),
        render: (f) => (f.appliance_type && f.appliance_type !== 'none'
          ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{f.appliance_type_label}</span>
              {f.appliance_version && <span className="list-hint list-code">{f.appliance_version}</span>}
            </span>
          )
          : dash),
      },
      {
        key: 'firmware_version',
        label: tr('fw.firmware', 'Firmware'),
        sortable: true,
        render: (f) => (f.firmware_version
          ? <span className="list-code">{f.firmware_version}</span>
          : dash),
      },
      {
        key: 'interfaces',
        label: tr('fw.iface_plural', 'Interfaces'),
        sortable: true,
        sortValue: (f) => f.interface_count ?? 0,
        render: (f) => (
          <button
            type="button"
            className="list-badge"
            style={{ cursor: 'pointer' }}
            onClick={(event) => { event.stopPropagation(); openIfaces(f); }}
            title={tr('fw.view_ifaces', 'Ver sus interfaces')}
          >
            {f.interface_count ?? 0}
          </button>
        ),
      },
      {
        key: 'ips',
        label: tr('fw.ips', 'IPs'),
        sortable: true,
        sortValue: (f) => f.ip_count ?? 0,
        render: (f) => (f.ip_count
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => { event.stopPropagation(); openIps(f); }}
              title={tr('fw.view_ips', 'Ver sus IPs asignadas')}
            >
              {f.ip_count}
            </button>
          )
          : <span className="list-hint">0</span>),
      },
      {
        key: 'ha',
        label: tr('fw.ha', 'HA'),
        sortable: true,
        sortValue: (f) => (f.ha_enabled ? 1 : 0),
        render: (f) => (f.ha_enabled
          ? <span className="list-badge list-badge--on">{HA_ROLE_LABELS[f.ha_role] || 'HA'}</span>
          : dash),
      },
      {
        key: 'location',
        label: tr('fw.location', 'Ubicación'),
        sortable: true,
        sortValue: (f) => pathFor(f.location_id) || f.location_name || '',
        render: (f) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span>{pathFor(f.location_id) || f.location_name || dash}</span>
            {f.rack_name && (
              <span className="list-hint">
                {f.rack_name}{f.position_u != null ? ` · U${f.position_u}` : ''}
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'serial_number',
        label: tr('fw.serial', 'Nº de serie'),
        sortable: true,
        render: (f) => (f.serial_number ? <span className="list-code">{f.serial_number}</span> : dash),
      },
      {
        key: 'services',
        label: tr('fw.services', 'Servicios'),
        sortable: true,
        sortValue: (f) => (f.services || []).length,
        render: (f) => {
          const lista = f.services || [];
          if (!lista.length) return dash;
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => { event.stopPropagation(); openServices(f); }}
              title={tr('fw.view_services', 'Ver sus servicios')}
            >
              {lista[0].name}{lista.length > 1 ? ` +${lista.length - 1}` : ''}
            </button>
          );
        },
      },
      {
        key: 'status',
        label: tr('fw.status', 'Estado'),
        sortable: true,
        sortValue: (f) => f.status || '',
        render: (f) => {
          const meta = STATUS_META[f.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{f.status_label || meta.label}</span>;
        },
      },
      {
        key: 'notes',
        label: tr('fw.notes', 'Notas'),
        render: (f) => f.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, openIfaces, openIps, openServices, pathFor]);

  const defaultVisible = [
    'name', 'deployment_type', 'vendor', 'appliance',
    'interfaces', 'ips', 'ha', 'status',
  ];

  /* ── filtros (el «agrupar por» va el último, como en el resto) ──────────── */
  const filters = [
    {
      key: 'status', label: tr('fw.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'deploy', label: tr('virt.type', 'Tipo'), allLabel: tr('fw.deployment_all', 'Físicos y virtuales'),
      value: filterDeploy, onChange: setFilterDeploy,
      options: [
        { value: 'physical', label: tr('fw.physical', 'Físico') },
        { value: 'virtual', label: tr('fw.virtual', 'Virtual (VM)') },
      ],
    },
    {
      key: 'vendor', label: tr('fw.vendor', 'Fabricante'), allLabel: tr('fw.vendor_all', 'Cualquier fabricante'),
      value: filterVendor, onChange: setFilterVendor, options: vendorOptions,
    },
    {
      key: 'appliance', label: tr('fw.appliance', 'Appliance'), allLabel: tr('fw.appliance_all', 'Cualquier appliance'),
      value: filterAppliance, onChange: setFilterAppliance, options: applianceOptions,
    },
    {
      key: 'ha', label: tr('fw.ha', 'HA'), allLabel: tr('fw.ha_all', 'Con o sin HA'),
      value: filterHa, onChange: setFilterHa,
      options: [
        { value: 'yes', label: tr('fw.ha_yes', 'Con HA') },
        { value: 'no', label: tr('fw.ha_no', 'Sin HA') },
        { value: 'primary', label: tr('fw.ha_primary', 'Solo primarios') },
        { value: 'secondary', label: tr('fw.ha_secondary', 'Solo secundarios') },
      ],
    },
    {
      key: 'vpn', label: tr('fw.vpn', 'VPN'), allLabel: tr('fw.vpn_all', 'VPN: da igual'),
      value: filterVpn, onChange: setFilterVpn,
      options: [
        { value: 'yes', label: tr('fw.vpn_yes', 'Con capacidad VPN') },
        { value: 'no', label: tr('fw.vpn_no', 'Sin capacidad VPN') },
      ],
    },
    {
      key: 'ids', label: tr('fw.ids', 'IDS/IPS'), allLabel: tr('fw.ids_all', 'IDS/IPS: da igual'),
      value: filterIds, onChange: setFilterIds,
      options: [
        { value: 'yes', label: tr('fw.ids_yes', 'Con IDS/IPS') },
        { value: 'no', label: tr('fw.ids_no', 'Sin IDS/IPS') },
      ],
    },
    {
      key: 'location', label: tr('fw.location', 'Ubicación'), allLabel: tr('fw.location_all', 'Cualquier ubicación'),
      value: filterLocation, onChange: setFilterLocation, options: locationOptions,
    },
    ...(hayRacks ? [{
      key: 'rack', label: tr('fw.rack', 'Rack'), allLabel: tr('fw.rack_all', 'Cualquier rack'),
      value: filterRack, onChange: setFilterRack, options: rackOptions,
    }] : []),
    {
      key: 'ifaces', label: tr('fw.iface_plural', 'Interfaces'), allLabel: tr('fw.ifaces_all', 'Con o sin interfaces'),
      value: filterIfaces, onChange: setFilterIfaces,
      options: [
        { value: 'yes', label: tr('fw.ifaces_yes', 'Con interfaces') },
        { value: 'no', label: tr('fw.ifaces_no', 'Sin interfaces') },
      ],
    },
    {
      key: 'serial', label: tr('fw.serial', 'Nº de serie'), allLabel: tr('fw.serial_all', 'Con o sin nº de serie'),
      value: filterSerial, onChange: setFilterSerial,
      options: [
        { value: 'yes', label: tr('fw.serial_yes', 'Con nº de serie') },
        { value: 'no', label: tr('fw.serial_no', 'Sin nº de serie') },
      ],
    },
    {
      key: 'service', label: tr('fw.services', 'Servicios'), allLabel: tr('fw.services_all', 'Con o sin servicio'),
      value: filterService, onChange: setFilterService, options: serviceOptions,
    },
    {
      key: 'group', label: tr('fw.group', 'Agrupar por'), allLabel: tr('fw.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'vendor', label: tr('fw.vendor', 'Fabricante') },
        { value: 'deployment', label: tr('virt.type', 'Tipo') },
        { value: 'location', label: tr('fw.location', 'Ubicación') },
        { value: 'appliance', label: tr('fw.appliance', 'Appliance') },
        { value: 'status', label: tr('fw.status', 'Estado') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'vendor') return (f) => f.vendor_label || tr('fw.no_vendor', 'Sin fabricante');
    if (groupMode === 'deployment') {
      return (f) => f.deployment_type_label
        || (f.deployment_type === 'virtual' ? tr('fw.virtual', 'Virtual') : tr('fw.physical', 'Físico'));
    }
    if (groupMode === 'location') return (f) => pathFor(f.location_id) || f.location_name || tr('fw.no_location', 'Sin ubicación asignada');
    if (groupMode === 'appliance') {
      return (f) => (f.appliance_type && f.appliance_type !== 'none'
        ? (f.appliance_type_label || f.appliance_type)
        : tr('fw.no_appliance', 'Sin appliance'));
    }
    if (groupMode === 'status') return (f) => f.status_label || (STATUS_META[f.status] || STATUS_META.active).label;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, tr, pathFor]);

  const subtitle = `${firewalls.length} ${tr('fw.plural', 'firewalls')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(f => ({
    'Nombre': f.name || '',
    'Hostname': f.hostname || '',
    'Tipo': f.deployment_type_label || (f.deployment_type === 'virtual' ? 'Virtual' : 'Físico'),
    'VM': f.vm_name || '',
    'Fabricante': f.vendor_label || '',
    'Modelo': f.model || '',
    'Appliance': (f.appliance_type && f.appliance_type !== 'none') ? (f.appliance_type_label || '') : '',
    'Appliance (versión)': f.appliance_version || '',
    'Firmware': f.firmware_version || '',
    'Nº de serie': f.serial_number || '',
    'Asset tag': f.asset_tag || '',
    'Throughput (Gbps)': f.throughput_gbps ?? '',
    'HA': f.ha_enabled ? (HA_ROLE_LABELS[f.ha_role] || 'Sí') : 'No',
    'VPN': f.vpn_capable ? 'Sí' : 'No',
    'IDS/IPS': f.ids_ips ? 'Sí' : 'No',
    'Interfaces': f.interface_count ?? 0,
    'IPs': f.ip_count ?? 0,
    'Ubicación': pathFor(f.location_id) || f.location_name || '',
    'Rack': f.rack_name || '',
    'Estado': f.status_label || (STATUS_META[f.status] || STATUS_META.active).label,
    'Servicios': (f.services || []).map(s => s.name).join(' · '),
    'Notas': f.notes || '',
  })), [filtered, pathFor]);

  /** Tarjetas: lo esencial de cada firewall, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((f) => {
          const meta = STATUS_META[f.status] || STATUS_META.active;
          return (
            <div
              key={f.id}
              id={`fw-card-${f.id}`}
              className={`list-card${f.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(f)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(f); }}
              title={`${tr('action.view', 'Ver')} ${f.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{f.name}</div>
                  {f.hostname && <span className="list-hint list-code">{f.hostname}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{f.status_label || meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">
                  {f.deployment_type_label
                    || (f.deployment_type === 'virtual' ? tr('fw.virtual', 'Virtual') : tr('fw.physical', 'Físico'))}
                </span>
                {f.vendor_label && <span>{f.vendor_label}</span>}
                {f.appliance_type && f.appliance_type !== 'none' && (
                  <span className="list-badge">{f.appliance_type_label}</span>
                )}
                <span className="list-hint">{f.interface_count ?? 0} {tr('fw.iface_short', 'if.')}</span>
                {f.ha_enabled && <span className="list-badge list-badge--on">HA {HA_ROLE_LABELS[f.ha_role] || ''}</span>}
                {f.location_name && <span className="list-hint">{pathFor(f.location_id) || f.location_name}</span>}
              </div>

              <div className="list-card-actions">{rowActions(f)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const FW_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada firewall'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, tr, hlId, pathFor]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('fw.select_company', 'Selecciona una empresa para ver los firewalls.')}
        </div>
      </div>
    );
  }

  const cardList = (servicesOf?.services || []);

  return (
    <>
      {(selected !== null) && (
        <FirewallForm
          firewall={selected && selected.id ? selected : null}
          companyId={companyId}
          initialMode={initialMode}
          startClone={startClone}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navFirewalls}
          navIndex={selected && selected.id ? navFirewalls.findIndex(f => String(f.id) === String(selected.id)) : -1}
          onNavigate={(destino) => { setStartClone(false); setSelected(destino); }}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={ShieldCheckIcon}
          title={tr('fw.title', 'Firewalls')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('fw.new', 'Nuevo firewall')}
          createTitle={tr('fw.new_hint', 'Firewall físico (en rack) o virtual (en una VM)')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('fw.search', 'Buscar por nombre, hostname, modelo, nº de serie…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="firewalls"
          exportSheetName="Firewalls"

          columns={columns}
          columnStorageKey="cmdb_cols_firewalls"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(f) => f.id}
          rowId={(f) => `fw-row-${f.id}`}
          rowClassName={(f) => (f.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(f) => `${tr('action.view', 'Ver')} ${f.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="firewalls"
                supportsRack
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  load();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="firewalls"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  load();
                }}
              />
            </>
          )}
          groupBy={groupBy}

          kpis={kpis}

          views={FW_VIEWS}
          listKey="firewalls"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando firewalls…')}
          error={error}

          empty={{
            icon: '🛡️',
            title: firewalls.length === 0
              ? tr('fw.empty', 'No hay firewalls')
              : tr('msg.no_results', 'Ningún firewall coincide con los filtros'),
            subtitle: firewalls.length === 0
              ? tr('fw.empty_hint', 'Registra aquí los firewalls físicos (montados en rack) y los virtuales (que corren en una VM).')
              : undefined,
            action: firewalls.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('fw.new', 'Nuevo firewall')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (f) => (
              <>
                {/* 1 · Interfaces */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openIfaces(f)}
                  title={tr('fw.view_ifaces', 'Ver sus interfaces')}
                >
                  <ArrowsRightLeftIcon />
                </button>
                {/* 2 · Conexiones (a qué switch / panel / toma va cada interfaz) */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openConns(f)}
                  title={tr('fw.view_conns', 'Ver a qué está conectado cada interfaz')}
                >
                  <LinkIcon />
                </button>
                {/* 3 · IPs del IPAM */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openIps(f)}
                  title={tr('fw.view_ips', 'Ver sus IPs asignadas')}
                >
                  <GlobeAltIcon />
                </button>
                {/* 4 · Servicios de los que forma parte */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(f.services || []).length}
                  onClick={() => openServices(f)}
                  title={(f.services || []).length
                    ? `${tr('fw.view_services', 'Ver sus servicios')} (${(f.services || []).length})`
                    : tr('fw.no_services', 'Este firewall no forma parte de ningún servicio')}
                >
                  <RectangleGroupIcon />
                </button>
                {/* 5 · Dónde está: rack (físico) o VM (virtual) */}
                {f.rack_id ? (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => navigate(`/infrastructure/rack/${f.rack_id}?destacar=${f.rack_element_id || ''}`)}
                    title={tr('fw.view_rack', 'Ver el rack donde está montado')}
                  >
                    <RectangleStackIcon />
                  </button>
                ) : null}
                {f.vm_id ? (
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => navigate(`/virt/vms?destacar=${f.vm_id}&abrir=1`)}
                    title={tr('fw.view_vm', 'Ver la VM donde corre')}
                  >
                    <ComputerDesktopIcon />
                  </button>
                ) : null}
                {/* 6 · Diseñador de servicios */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(`/services/designer?ci_type=firewall&ci_id=${f.id}&ci_name=${encodeURIComponent(f.name || '')}`)}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
              </>
            ),
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('fw.delete_title', 'Eliminar firewall'),
            deleteMessage: (f) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{f.name}</strong>?{' '}
                {tr('fw.delete_warning', 'Se eliminarán también sus interfaces y las asignaciones de IP del IPAM.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo: interfaces ─═ */}
      {ifacesOf && (
        <Dialog
          title={`${tr('fw.ifaces_of', 'Interfaces de')} ${ifacesOf.name || ''}`}
          icon={<ArrowsRightLeftIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          onClose={() => setIfacesOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setIfacesOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {ifacesLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!ifacesLoading && ifaces.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('fw.no_ifaces', 'Este firewall no tiene interfaces declaradas.')}
            </p>
          )}
          {!ifacesLoading && ifaces.length > 0 && ifaces.map(i => (
            <Row key={i.id}>
              <span style={{ fontWeight: 600, minWidth: 90 }}>{i.name}</span>
              <span className="list-badge">{i.iface_type_label || i.iface_type || '—'}</span>
              {i.connector_type && <span className="list-code">{i.connector_type_label || i.connector_type}</span>}
              {i.mac_address && <span className="list-code">{i.mac_address}</span>}
              {i.speed_mbps ? <span className="list-hint">{i.speed_mbps} Mbps</span> : null}
              {i.vlan_id ? <span className="list-badge">VLAN {i.vlan_id}</span> : null}
              {i.description && <span className="list-hint">{i.description}</span>}
              {i.enabled === false && (
                <span className="list-badge list-badge--off" style={{ marginLeft: 'auto' }}>
                  {tr('fw.iface_down', 'Deshabilitada')}
                </span>
              )}
            </Row>
          ))}
        </Dialog>
      )}

      {/* ══ Diálogo: conexiones (el otro extremo del cable) ══ */}
      {connsOf && (
        <Dialog
          title={`${tr('fw.conns_of', 'Conexiones de')} ${connsOf.name || ''}`}
          icon={<LinkIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          onClose={() => setConnsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setConnsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {connsLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!connsLoading && conns.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('fw.no_ifaces', 'Este firewall no tiene interfaces declaradas.')}
            </p>
          )}
          {!connsLoading && conns.length > 0 && conns.map(p => {
            const otro = p.connection?.other_end;
            return (
              <Row key={p.id}>
                <span style={{ fontWeight: 600, minWidth: 90 }}>{p.label || `P${p.port_number}`}</span>
                {p.speed && <span className="list-hint">{p.speed}</span>}
                {otro ? (
                  <>
                    <span className="list-badge list-badge--on">
                      {tr('fw.connected', 'Conectado a')}: {otro.element_name || '—'}
                      {otro.port_type !== 'wall_outlet' && otro.port_number != null
                        ? ` · P${otro.port_number}`
                        : (otro.label ? ` · ${otro.label}` : '')}
                    </span>
                    {p.connection?.cable_type && (
                      <span className="list-hint">
                        {tr('fw.cable', 'Cable')}: {p.connection.cable_type}
                        {p.connection.cable_color ? ` · ${p.connection.cable_color}` : ''}
                        {p.connection.cable_length_m ? ` · ${p.connection.cable_length_m} m` : ''}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="list-badge list-badge--off">{tr('fw.free', 'Libre')}</span>
                )}
              </Row>
            );
          })}
        </Dialog>
      )}

      {/* ══ Diálogo: IPs asignadas en el IPAM ══ */}
      {ipsOf && (
        <Dialog
          title={`${tr('fw.ips_of', 'IPs asignadas a')} ${ipsOf.name || ''}`}
          icon={<GlobeAltIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          onClose={() => setIpsOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setIpsOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {ipsLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!ipsLoading && ips.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('fw.no_ips', 'Este firewall no tiene IPs asignadas en el IPAM.')}
            </p>
          )}
          {!ipsLoading && ips.length > 0 && ips.map(a => (
            <Row key={a.id}>
              <span className="list-code" style={{ fontWeight: 600, minWidth: 130 }}>{a.ip || '—'}</span>
              {a.subnet_cidr && <span className="list-badge">{a.subnet_cidr}</span>}
              {a.interface_ref && <span className="list-hint">{a.interface_ref}</span>}
              {a.is_primary && <span className="list-badge list-badge--on">{tr('fw.primary', 'Principal')}</span>}
              {a.dns_name && <span className="list-hint list-code">{a.dns_name}</span>}
              <button
                type="button"
                className="list-action-btn"
                style={{ marginLeft: 'auto' }}
                onClick={() => navigate(`/networks/ips?destacar=${a.ip_id}&abrir=1`)}
                title={tr('fw.open_in_ipam', 'Ver la IP en el IPAM')}
              >
                <GlobeAltIcon />
              </button>
            </Row>
          ))}
        </Dialog>
      )}

      {/* ══ Diálogo: servicios de los que forma parte ══ */}
      {servicesOf && (
        <Dialog
          title={`${tr('fw.services_of', 'Servicios de')} ${servicesOf.name || ''}`}
          icon={<RectangleGroupIcon style={{ width: 18, height: 18 }} />}
          maxWidth={620}
          onClose={() => setServicesOf(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setServicesOf(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {cardList.length === 0 && (
            <p className="list-hint" style={{ margin: 0 }}>
              {tr('fw.no_services', 'Este firewall no forma parte de ningún servicio.')}
            </p>
          )}
          {cardList.length > 0 && cardList.map(s => (
            <Row key={s.id}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              {s.role && <span className="list-badge">{s.role}</span>}
              <span className="list-hint">
                {s.origin === 'host'
                  ? tr('fw.origin_host', 'Servicio en el que corre')
                  : tr('fw.origin_designer', 'Nodo del Diseñador')}
              </span>
              <button
                type="button"
                className="list-action-btn"
                style={{ marginLeft: 'auto' }}
                onClick={() => navigate(`/services?destacar=${s.id}&abrir=1`)}
                title={tr('fw.view_service', 'Ver el servicio')}
              >
                <ShieldExclamationIcon />
              </button>
            </Row>
          ))}
        </Dialog>
      )}
    </>
  );
}
